import type {
  ClosingTaskOwner,
  ClosingTaskView,
  DocumentView,
  FeeEntryView,
  FundingEventView,
  MoneyView,
  NewClosingTask,
  PayeeCategory,
  RecordFeeEntryBody,
  RecordFundingEventBody,
  TexClosingView,
  TexClosingTaskView,
} from '@tex/finance-shared'
import { CLOSING_TASK_OWNERS, PAYEE_CATEGORIES, parseDecimal } from '@tex/finance-shared'
import { type FormEvent, useState } from 'react'
import { ApiError, errorMessage } from '../lib/api'
import {
  type ClosingTaskVerb,
  FEE_ENTRY_LABELS,
  FUNDING_KIND_LABELS,
  PAYEE_LABELS,
  TASK_OWNER_LABELS,
  taskActions,
  useAddClosingTask,
  useApproveDealerTaskText,
  useClosing,
  useClosingTaskCommand,
  useCompleteClosingTask,
  useEnterClosing,
  useFees,
  useFunding,
  useRecordFee,
  useRecordFunding,
  useReevaluateClosing,
} from '../lib/closing'
import { useDocuments } from '../lib/documents'
import { OFFER_STATUS_LABELS } from '../lib/offers'
import { formatAmount, formatDate, formatDay, humanize } from '../lib/format'
import { EvidenceLink, ReadinessSummary, TaskList } from './closing-card'
import { OffersCard } from './offers-card'
import { Alert, Button, Card, Field, Input, Mono, Row, Select, Textarea } from './ui'

/**
 * Tex's closing workspace: readiness → enterClosing → tasks → funding → fees. Every button maps to
 * one named server command; nothing here changes a stage on its own. Reviewers run closing, only
 * ops records money facts and sees the fee ledger.
 */
export function ClosingTab({ caseId, opsAdmin }: { caseId: string; opsAdmin: boolean }) {
  const closing = useClosing(caseId)
  if (closing.isPending) return <p className="text-sm text-neutral-500">Loading closing…</p>
  if (closing.isError) return <Alert tone="error">{errorMessage(closing.error)}</Alert>
  const view = closing.data
  if (view.audience !== 'tex') return <Alert tone="error">This view is only rendered from the Tex projection.</Alert>

  const taskStage = view.stage === 'closing' || view.stage === 'funded'
  return (
    <div className="space-y-6">
      <ReadinessCard caseId={caseId} view={view} />
      {(view.stage === 'offer_review' || view.stage === 'closing') && <OffersCard caseId={caseId} audience="tex" stage={view.stage} />}
      <TasksCard caseId={caseId} view={view} canManage={taskStage} />
      <FundingCard caseId={caseId} view={view} canRecord={opsAdmin} />
      {opsAdmin ? (
        <FeeLedgerCard caseId={caseId} view={view} />
      ) : (
        <Card title="Fee ledger">
          <p className="text-sm text-neutral-500">What Tex is owed and has collected is visible to ops admins only.</p>
        </Card>
      )}
    </div>
  )
}

function useIdempotencyKey() {
  const [key, setKey] = useState(() => crypto.randomUUID())
  return { key, rotate: () => setKey(crypto.randomUUID()) }
}

// ── readiness + enterClosing + reevaluate ────────────────────────────────────

/**
 * A stage-changing command that lost a version race unmounts its own form once the view refetches,
 * so the conflict is shown here, on the card that stays mounted, until the reviewer dismisses it.
 */
function useConflictNotice() {
  const [notice, setNotice] = useState<string | null>(null)
  return {
    notice,
    dismiss: () => setNotice(null),
    onConflict: (err: unknown) => {
      if (!(err instanceof ApiError) || err.code !== 'version_conflict') return false
      setNotice(`This case changed while you were working (${err.reason ?? err.code}). The view below has been refreshed; nothing was submitted.`)
      return true
    },
  }
}

function ReadinessCard({ caseId, view }: { caseId: string; view: TexClosingView }) {
  const o = view.selected_offer
  const conflict = useConflictNotice()
  return (
    <Card title="Closing readiness">
      {conflict.notice && (
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex-1">
            <Alert tone="error">{conflict.notice}</Alert>
          </div>
          <Button type="button" variant="ghost" onClick={conflict.dismiss}>
            Dismiss
          </Button>
        </div>
      )}
      <ReadinessSummary readiness={view.readiness} funding={view.funding} />
      {o ? (
        <dl className="mt-3 grid gap-x-6 sm:grid-cols-2">
          <Row label="Selected offer" value={`${o.partner_display_name} · ${o.terms.product_type}`} />
          <Row label="Approved amount" value={<Mono>{formatAmount(o.terms.approved_amount)}</Mono>} />
          <Row label="Offer status" value={OFFER_STATUS_LABELS[o.status]} />
          <Row label="Expires" value={o.terms.expires_at ? formatDay(o.terms.expires_at) : 'no expiry stated'} />
        </dl>
      ) : (
        <p className="mt-3 text-sm text-neutral-500">No offer is selected.</p>
      )}
      <p className="mt-2 text-xs text-neutral-500">
        Tasks: {view.task_summary.open} open · {view.task_summary.blocked} blocked · {view.task_summary.complete} complete · {view.task_summary.waived} waived
      </p>
      {view.readiness.status === 'ready_to_close' && (
        <EnterClosingForm key={view.selected_offer?.id ?? 'none'} caseId={caseId} view={view} onConflict={conflict.onConflict} />
      )}
      {view.stage === 'closing' && view.readiness.reevaluation_required && (
        <ReevaluateForm caseId={caseId} view={view} onConflict={conflict.onConflict} />
      )}
    </Card>
  )
}

interface TaskDraft {
  title: string
  description: string
  owner: ClosingTaskOwner
  due_on: string
}

const emptyTask = (title = '', owner: ClosingTaskOwner = 'borrower'): TaskDraft => ({ title, description: '', owner, due_on: '' })

function toNewTask(d: TaskDraft): NewClosingTask {
  return {
    title: d.title.trim(),
    owner: d.owner,
    ...(d.description.trim() ? { description: d.description.trim() } : {}),
    ...(d.due_on ? { due_on: d.due_on } : {}),
  }
}

function TaskDraftFields({ draft, onChange }: { draft: TaskDraft; onChange: (d: TaskDraft) => void }) {
  return (
    <div className="grid gap-2 sm:grid-cols-[1fr_140px_150px]">
      <Field label="Task">
        <Input value={draft.title} onChange={(e) => onChange({ ...draft, title: e.target.value })} required maxLength={500} />
      </Field>
      <Field label="Owner">
        <Select value={draft.owner} onChange={(e) => onChange({ ...draft, owner: e.target.value as ClosingTaskOwner })}>
          {CLOSING_TASK_OWNERS.map((o) => (
            <option key={o} value={o}>
              {TASK_OWNER_LABELS[o]}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Due">
        <Input type="date" value={draft.due_on} onChange={(e) => onChange({ ...draft, due_on: e.target.value })} />
      </Field>
      <div className="sm:col-span-3">
        <Field label="Detail (optional)">
          <Input value={draft.description} onChange={(e) => onChange({ ...draft, description: e.target.value })} maxLength={5000} />
        </Field>
      </div>
    </div>
  )
}

/** `enterClosing`: the partner's conditions are pre-listed as tasks so none is forgotten; the reviewer edits owners and dates. */
function EnterClosingForm({ caseId, view, onConflict }: { caseId: string; view: TexClosingView; onConflict: (err: unknown) => boolean }) {
  const enter = useEnterClosing(caseId)
  const [open, setOpen] = useState(false)
  const [tasks, setTasks] = useState<TaskDraft[]>(() => (view.selected_offer?.terms.conditions ?? []).map((c) => emptyTask(c, 'borrower')))
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await enter.mutateAsync({
        expected_version: view.case_version,
        tasks: tasks.filter((t) => t.title.trim()).map(toNewTask),
        ...(note.trim() ? { note: note.trim() } : {}),
      })
      setOpen(false)
    } catch (err) {
      if (!onConflict(err)) setError(errorMessage(err))
    }
  }

  if (!open)
    return (
      <div className="mt-4">
        <Button type="button" onClick={() => setOpen(true)}>
          Enter closing
        </Button>
      </div>
    )

  return (
    <form onSubmit={submit} className="mt-4 space-y-3 rounded-md bg-neutral-50 p-4">
      <p className="text-sm">
        Moves the case to <strong>closing</strong> on the selected offer. Start with the partner's conditions as tasks; you can add more later.
      </p>
      {tasks.map((t, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="flex-1">
            <TaskDraftFields draft={t} onChange={(d) => setTasks(tasks.map((x, j) => (j === i ? d : x)))} />
          </div>
          <Button type="button" variant="ghost" onClick={() => setTasks(tasks.filter((_, j) => j !== i))}>
            Remove
          </Button>
        </div>
      ))}
      <Button type="button" variant="secondary" onClick={() => setTasks([...tasks, emptyTask()])}>
        Add task
      </Button>
      <Field label="Note (optional)">
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} minLength={3} maxLength={2000} />
      </Field>
      {error && <Alert tone="error">{error}</Alert>}
      <div className="flex gap-2">
        <Button type="submit" disabled={enter.isPending}>
          Confirm: enter closing
        </Button>
        <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={enter.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

/** `reevaluateClosing`: the selected offer stopped holding; the case goes back to offer review (or to the partners). */
function ReevaluateForm({ caseId, view, onConflict }: { caseId: string; view: TexClosingView; onConflict: (err: unknown) => boolean }) {
  const reevaluate = useReevaluateClosing(caseId)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await reevaluate.mutateAsync({ expected_version: view.case_version, note: note.trim() })
    } catch (err) {
      if (!onConflict(err)) setError(errorMessage(err))
    }
  }
  return (
    <form onSubmit={submit} className="mt-4 space-y-2 rounded-md bg-red-50 p-4">
      <p className="text-sm">Ends this closing and sends the case back for the buyer to re-select (or back to partners if nothing is open).</p>
      <Field label="Why">
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} required minLength={3} maxLength={2000} />
      </Field>
      {error && <Alert tone="error">{error}</Alert>}
      <Button type="submit" variant="danger" disabled={reevaluate.isPending}>
        Re-evaluate closing
      </Button>
    </form>
  )
}

// ── tasks ────────────────────────────────────────────────────────────────────

function TasksCard({ caseId, view, canManage }: { caseId: string; view: TexClosingView; canManage: boolean }) {
  const docs = useDocuments(caseId)
  const available = docs.data?.documents.filter((d) => d.availability === 'available') ?? []
  const [adding, setAdding] = useState(false)
  return (
    <Card
      title="Closing tasks"
      actions={
        canManage ? (
          <Button type="button" variant="secondary" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Cancel' : 'Add task'}
          </Button>
        ) : undefined
      }
    >
      {adding && <AddTaskForm caseId={caseId} onDone={() => setAdding(false)} />}
      {!canManage && view.stage !== 'closed' && <p className="text-sm text-neutral-500">Tasks are managed once the case is in closing.</p>}
      <TaskList caseId={caseId} tasks={view.tasks} mine="tex" renderActions={(t) => (
        <>
          {canManage && <TaskActions caseId={caseId} task={t} documents={available} />}
          {t.owner === 'dealer' && <DealerTaskText caseId={caseId} task={t} canManage={canManage} />}
        </>
      )} />
    </Card>
  )
}

function AddTaskForm({ caseId, onDone }: { caseId: string; onDone: () => void }) {
  const add = useAddClosingTask(caseId)
  const [draft, setDraft] = useState<TaskDraft>(emptyTask)
  const [error, setError] = useState<string | null>(null)
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await add.mutateAsync({ task: toNewTask(draft) })
      onDone()
    } catch (err) {
      setError(errorMessage(err))
    }
  }
  return (
    <form onSubmit={submit} className="mb-4 space-y-2 rounded-md bg-neutral-50 p-3">
      <TaskDraftFields draft={draft} onChange={setDraft} />
      {error && <Alert tone="error">{error}</Alert>}
      <Button type="submit" disabled={add.isPending}>
        Add
      </Button>
    </form>
  )
}

type TaskAction = 'complete' | ClosingTaskVerb

function DealerTaskText({ caseId, task, canManage }: { caseId: string; task: TexClosingTaskView; canManage: boolean }) {
  const approve = useApproveDealerTaskText(caseId)
  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const text = task.dealer_text
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState(task.updated_at)
  const edit = () => {
    setTitle(text?.title ?? '')
    setDescription(text?.description ?? '')
    setNote(text?.note ?? '')
    setError(null)
    setExpectedUpdatedAt(task.updated_at)
    setEditing(true)
  }
  const save = async (revoke: boolean) => {
    setError(null)
    try {
      await approve.mutateAsync({ taskId: task.id, body: {
        expected_updated_at: revoke ? task.updated_at : expectedUpdatedAt,
        dealer_text: revoke ? null : {
          title: title.trim(),
          ...(description.trim() ? { description: description.trim() } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      } })
      setEditing(false)
    } catch (err) {
      setError(errorMessage(err))
      if (err instanceof ApiError && err.code === 'task_changed') setEditing(false)
    }
  }
  const submit = (e: FormEvent) => {
    e.preventDefault()
    void save(false)
  }
  return (
    <div className="mt-2 space-y-2 rounded-md border border-neutral-200 p-3">
      <p className="text-xs font-medium text-neutral-600">Dealer-visible text</p>
      {text ? (
        <div className="text-sm text-neutral-700">
          <p>{text.title}</p>
          {text.description && <p>{text.description}</p>}
          {text.note && <p>{text.note}</p>}
          <p className="mt-1 text-xs text-neutral-500">Approved by {text.approved_by_user_id} · {formatDate(text.approved_at)}</p>
        </div>
      ) : <p className="text-sm text-neutral-500">The dealer sees “Closing task {task.position}” until Tex approves separate text.</p>}
      {error && <Alert tone="error">{error}</Alert>}
      {editing ? (
        <form onSubmit={submit} className="space-y-2">
          <Field label="Dealer task title">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={500} />
          </Field>
          <Field label="Dealer instructions (optional)">
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={5000} rows={2} />
          </Field>
          <Field label="Dealer status note (optional)" hint="Cleared when task status changes; approve a new note after the change.">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} minLength={3} maxLength={2000} rows={2} />
          </Field>
          <label className="flex items-start gap-2 text-sm text-neutral-700">
            <input type="checkbox" required className="mt-1" />
            <span>I approve this text for the dealer. It contains no funding partner identity or private offer terms.</span>
          </label>
          <div className="flex gap-2">
            <Button type="submit" disabled={approve.isPending}>Approve dealer text</Button>
            <Button type="button" variant="ghost" disabled={approve.isPending} onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </form>
      ) : canManage && (
        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={edit} disabled={approve.isPending}>{text ? 'Edit dealer text' : 'Approve dealer text'}</Button>
          {text && <Button type="button" variant="ghost" onClick={() => void save(true)} disabled={approve.isPending}>Revoke dealer text</Button>}
        </div>
      )}
    </div>
  )
}

function TaskActions({ caseId, task, documents }: { caseId: string; task: ClosingTaskView; documents: DocumentView[] }) {
  const complete = useCompleteClosingTask(caseId)
  const command = useClosingTaskCommand(caseId)
  const [action, setAction] = useState<TaskAction | null>(null)
  const [evidence, setEvidence] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const allowed = taskActions(task.status)
  const busy = complete.isPending || command.isPending

  const reset = () => {
    setAction(null)
    setEvidence('')
    setNote('')
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!action) return
    setError(null)
    try {
      if (action === 'complete') {
        await complete.mutateAsync({ taskId: task.id, body: { evidence_document_id: evidence, ...(note.trim() ? { note: note.trim() } : {}) } })
      } else {
        await command.mutateAsync({ taskId: task.id, verb: action, body: { note: note.trim() } })
      }
      reset()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  if (action === null)
    return (
      <div className="flex flex-wrap gap-2 pt-1">
        {allowed.complete && (
          <Button type="button" variant="secondary" onClick={() => setAction('complete')}>
            Complete
          </Button>
        )}
        {allowed.waive && (
          <Button type="button" variant="ghost" onClick={() => setAction('waive')}>
            Waive
          </Button>
        )}
        {allowed.block && (
          <Button type="button" variant="ghost" onClick={() => setAction('block')}>
            Block
          </Button>
        )}
        {allowed.reopen && (
          <Button type="button" variant="ghost" onClick={() => setAction('reopen')}>
            Reopen
          </Button>
        )}
      </div>
    )

  return (
    <form onSubmit={submit} className="mt-2 space-y-2 rounded-md bg-neutral-50 p-3">
      {action === 'complete' && (
        <Field label="Evidence document" hint="The file that proves the task was done. Only clean, stored documents can be chosen.">
          <Select value={evidence} onChange={(e) => setEvidence(e.target.value)} required>
            <option value="">Choose a document…</option>
            {documents.map((d) => (
              <option key={d.id} value={d.id}>
                {d.original_filename} · {humanize(d.document_type)}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <Field label={action === 'complete' ? 'Note (optional)' : 'Reason'}>
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} required={action !== 'complete'} minLength={3} maxLength={2000} />
      </Field>
      {error && <Alert tone="error">{error}</Alert>}
      <div className="flex gap-2">
        <Button type="submit" disabled={busy}>
          {humanize(action)}
        </Button>
        <Button type="button" variant="ghost" onClick={reset} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

// ── funding ──────────────────────────────────────────────────────────────────

function FundingCard({ caseId, view, canRecord }: { caseId: string; view: TexClosingView; canRecord: boolean }) {
  const funding = useFunding(caseId)
  const [recording, setRecording] = useState(false)
  const recordable = canRecord && (view.stage === 'closing' || view.stage === 'funded' || view.stage === 'closed')
  return (
    <Card
      title="Funding"
      actions={
        recordable ? (
          <Button type="button" variant="secondary" onClick={() => setRecording((v) => !v)}>
            {recording ? 'Cancel' : 'Record funding event'}
          </Button>
        ) : undefined
      }
    >
      {funding.isPending && <p className="text-sm text-neutral-500">Loading…</p>}
      {funding.isError && <Alert tone="error">{errorMessage(funding.error)}</Alert>}
      {funding.data && (
        <>
          {recording && funding.data.audience === 'tex' && (
            <RecordFundingForm caseId={caseId} view={view} events={funding.data.events} onDone={() => setRecording(false)} />
          )}
          <dl className="grid gap-x-6 sm:grid-cols-2">
            <Row label="Net partner funded" value={<Mono>{formatAmount(funding.data.summary.net_partner_funded)}</Mono>} />
            <Row label="Net seller received" value={<Mono>{formatAmount(funding.data.summary.net_dealer_received)}</Mono>} />
            <Row label="Partner confirmed" value={funding.data.summary.partner_confirmed ? 'yes' : 'no'} />
            <Row label="Seller receipt confirmed" value={funding.data.summary.dealer_receipt_confirmed ? 'yes' : 'no'} />
            <Row label="First funded" value={formatDay(funding.data.summary.first_funded_on)} />
          </dl>
          <FundingEventList caseId={caseId} events={funding.data.events} />
        </>
      )}
      <p className="mt-3 text-xs text-neutral-400">
        Tex holds no money. Only the partner's evidenced confirmation moves a case to funded; a signed document or an approval never does.
        {!canRecord && ' Recording funding events is ops-admin only.'}
      </p>
    </Card>
  )
}

function FundingEventList({ caseId, events }: { caseId: string; events: FundingEventView[] }) {
  if (events.length === 0) return <p className="mt-3 text-sm text-neutral-500">No funding events recorded.</p>
  return (
    <ul className="mt-3 divide-y divide-neutral-100 text-sm">
      {events.map((e) => (
        <li key={e.id} className="space-y-0.5 py-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span>
              <span className="font-medium">{FUNDING_KIND_LABELS[e.kind]}</span>
              {e.partner_display_name && <span className="text-neutral-500"> · {e.partner_display_name}</span>}
              {e.payee_category && <span className="text-neutral-500"> · paid to {humanize(e.payee_category)}</span>}
              {e.funded_on && <span className="text-neutral-500"> · {formatDay(e.funded_on)}</span>}
              {e.synthetic && <span className="text-neutral-400"> · synthetic</span>}
            </span>
            <Mono className={e.amount.amount_minor < 0 ? 'text-red-700' : ''}>{formatAmount(e.amount)}</Mono>
          </div>
          <div className="text-xs text-neutral-500">
            {e.external_reference && <span>Ref {e.external_reference} · </span>}
            {e.corrects_event_id && (
              <span>
                Adjusts <Mono>{e.corrects_event_id.slice(0, 8)}</Mono> ·{' '}
              </span>
            )}
            {e.evidence_document_id && (
              <>
                <EvidenceLink caseId={caseId} documentId={e.evidence_document_id} /> ·{' '}
              </>
            )}
            <span>
              Recorded {formatDate(e.recorded_at)} · <Mono>{e.id.slice(0, 8)}</Mono>
            </span>
            {e.note && <p className="text-neutral-700">{e.note}</p>}
          </div>
        </li>
      ))}
    </ul>
  )
}

type FundingKind = RecordFundingEventBody['kind']

function parseMoney(input: string, field: string): MoneyView {
  try {
    const m = parseDecimal(input)
    return { amount_minor: m.amountMinor, currency: m.currency }
  } catch {
    throw new Error(`${field}: enter a dollar amount like 125000.00`)
  }
}

function RecordFundingForm({ caseId, view, events, onDone }: { caseId: string; view: TexClosingView; events: FundingEventView[]; onDone: () => void }) {
  const record = useRecordFunding(caseId)
  const docs = useDocuments(caseId)
  const idem = useIdempotencyKey()
  // Confirmations are accepted in closing/funded only; a closed case takes adjustments alone.
  const confirmable = view.stage === 'closing' || view.stage === 'funded'
  const [kind, setKind] = useState<FundingKind>(view.stage === 'closing' ? 'partner_confirmed_funding' : confirmable ? 'dealer_confirmed_receipt' : 'correction')
  const [amount, setAmount] = useState('')
  const [fundedOn, setFundedOn] = useState('')
  const [payee, setPayee] = useState<PayeeCategory>('seller')
  const [evidence, setEvidence] = useState('')
  const [reference, setReference] = useState('')
  const [corrects, setCorrects] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const available = docs.data?.documents.filter((d) => d.availability === 'available') ?? []
  const reversed = new Set(events.filter((e) => e.kind === 'reversal').map((e) => e.corrects_event_id))
  const correctable = events.filter((e) => (e.kind === 'partner_confirmed_funding' || e.kind === 'dealer_confirmed_receipt') && !reversed.has(e.id))
  const netOf = (target: FundingEventView) => ({
    ...target.amount,
    amount_minor: events.filter((e) => e.corrects_event_id === target.id).reduce((sum, e) => sum + e.amount.amount_minor, target.amount.amount_minor),
  })

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const trimmedNote = note.trim()
      const optional = {
        ...(reference.trim() ? { external_reference: reference.trim() } : {}),
        ...(trimmedNote ? { note: trimmedNote } : {}),
      }
      let body: RecordFundingEventBody
      if (kind === 'partner_confirmed_funding') {
        body = { kind, expected_version: view.case_version, amount: parseMoney(amount, 'Amount'), funded_on: fundedOn, payee_category: payee, evidence_document_id: evidence, ...optional }
      } else if (kind === 'dealer_confirmed_receipt') {
        body = { kind, amount: parseMoney(amount, 'Amount'), funded_on: fundedOn, evidence_document_id: evidence, ...optional }
      } else {
        if (!trimmedNote) throw new Error('A correction or reversal needs a note')
        body = {
          kind,
          corrects_event_id: corrects,
          amount: parseMoney(amount, 'Amount'),
          note: trimmedNote,
          ...(evidence ? { evidence_document_id: evidence } : {}),
          ...(reference.trim() ? { external_reference: reference.trim() } : {}),
        }
      }
      await record.mutateAsync({ body, idempotencyKey: idem.key })
      idem.rotate()
      onDone()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const isAdjustment = kind === 'correction' || kind === 'reversal'
  const target = correctable.find((e) => e.id === corrects)
  return (
    <form onSubmit={submit} className="mb-4 space-y-3 rounded-md bg-neutral-50 p-4">
      <Field label="What happened">
        <Select value={kind} onChange={(e) => setKind(e.target.value as FundingKind)}>
          {confirmable && (
            <>
              <option value="partner_confirmed_funding">{FUNDING_KIND_LABELS.partner_confirmed_funding} (moves the case to funded)</option>
              <option value="dealer_confirmed_receipt">{FUNDING_KIND_LABELS.dealer_confirmed_receipt}</option>
            </>
          )}
          <option value="correction">{FUNDING_KIND_LABELS.correction} (signed delta to an earlier event)</option>
          <option value="reversal">{FUNDING_KIND_LABELS.reversal} (exact negative of an earlier event)</option>
        </Select>
      </Field>
      {isAdjustment && (
        <Field label="Adjusts event">
          <Select value={corrects} onChange={(e) => setCorrects(e.target.value)} required>
            <option value="">Choose the confirmation…</option>
            {correctable.map((e) => (
              <option key={e.id} value={e.id}>
                {FUNDING_KIND_LABELS[e.kind]} · {formatAmount(e.amount)} · {formatDay(e.funded_on)} · {e.id.slice(0, 8)}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={isAdjustment ? 'Signed amount (USD)' : 'Amount (USD)'} hint={
            target && kind === 'reversal'
              ? `A reversal of this event and its corrections is exactly -${formatAmount(netOf(target))}`
              : target && kind === 'correction'
                ? `This event nets to ${formatAmount(netOf(target))}; a correction must leave it above zero — reverse it if the money never moved`
                : undefined
          }>
          <Input value={amount} onChange={(e) => setAmount(e.target.value)} required placeholder={isAdjustment ? '-1250.00' : '125000.00'} inputMode="decimal" />
        </Field>
        {!isAdjustment && (
          <Field label="Funded on">
            <Input type="date" value={fundedOn} onChange={(e) => setFundedOn(e.target.value)} required />
          </Field>
        )}
        {kind === 'partner_confirmed_funding' && (
          <Field label="Partner paid">
            <Select value={payee} onChange={(e) => setPayee(e.target.value as PayeeCategory)}>
              {PAYEE_CATEGORIES.map((p) => (
                <option key={p} value={p}>
                  {PAYEE_LABELS[p]}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="External reference (optional)" hint="Partner or bank reference as written on the confirmation.">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} />
        </Field>
      </div>
      <Field
        label={isAdjustment ? 'Evidence document (optional)' : 'Evidence document'}
        hint={kind === 'partner_confirmed_funding' ? 'The partner’s funding confirmation. Required: this is what moves the case to funded.' : kind === 'dealer_confirmed_receipt' ? 'The seller’s remittance or bank confirmation.' : undefined}
      >
        <Select value={evidence} onChange={(e) => setEvidence(e.target.value)} required={!isAdjustment}>
          <option value="">{isAdjustment ? 'None' : 'Choose a document…'}</option>
          {available.map((d) => (
            <option key={d.id} value={d.id}>
              {d.original_filename} · {humanize(d.document_type)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={isAdjustment || payee === 'other' ? 'Note' : 'Note (optional)'}>
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} required={isAdjustment || (kind === 'partner_confirmed_funding' && payee === 'other')} minLength={3} maxLength={2000} />
      </Field>
      {error && <Alert tone="error">{error}</Alert>}
      <Button type="submit" disabled={record.isPending}>
        Record
      </Button>
    </form>
  )
}

// ── fees ─────────────────────────────────────────────────────────────────────

function FeeLedgerCard({ caseId, view }: { caseId: string; view: TexClosingView }) {
  const fees = useFees(caseId)
  const funding = useFunding(caseId)
  const [recording, setRecording] = useState(false)
  const recordable = view.stage === 'closing' || view.stage === 'funded' || view.stage === 'closed'
  return (
    <Card
      title="Fee ledger"
      actions={
        recordable ? (
          <Button type="button" variant="secondary" onClick={() => setRecording((v) => !v)}>
            {recording ? 'Cancel' : 'Record fee entry'}
          </Button>
        ) : undefined
      }
    >
      {fees.isPending && <p className="text-sm text-neutral-500">Loading…</p>}
      {fees.isError && <Alert tone="error">{errorMessage(fees.error)}</Alert>}
      {fees.data && (
        <>
          {recording && <RecordFeeForm caseId={caseId} entries={fees.data.entries} events={funding.data?.events ?? []} onDone={() => setRecording(false)} />}
          <dl className="grid gap-x-6 sm:grid-cols-3">
            <Row label="Expected" value={<Mono>{formatAmount(fees.data.summary.expected)}</Mono>} />
            <Row label="Collected" value={<Mono>{formatAmount(fees.data.summary.collected)}</Mono>} />
            <Row label="Outstanding" value={<Mono className="font-semibold">{formatAmount(fees.data.summary.outstanding)}</Mono>} />
          </dl>
          <FeeEntryList caseId={caseId} entries={fees.data.entries} />
        </>
      )}
      <p className="mt-3 text-xs text-neutral-400">Append-only. Collected stays at zero until a receipt with its own evidence is recorded; nothing is inferred from funding.</p>
    </Card>
  )
}

function FeeEntryList({ caseId, entries }: { caseId: string; entries: FeeEntryView[] }) {
  if (entries.length === 0) return <p className="mt-3 text-sm text-neutral-500">No fee entries recorded.</p>
  return (
    <ul className="mt-3 divide-y divide-neutral-100 text-sm">
      {entries.map((e) => (
        <li key={e.id} className="space-y-0.5 py-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span>
              <span className="font-medium">{FEE_ENTRY_LABELS[e.entry_type]}</span>
              {e.adjusts && <span className="text-neutral-500"> · adjusts {FEE_ENTRY_LABELS[e.adjusts].toLowerCase()}</span>}
              {e.basis && <span className="text-neutral-500"> · {e.basis}</span>}
              {e.synthetic && <span className="text-neutral-400"> · synthetic</span>}
            </span>
            <Mono className={e.amount.amount_minor < 0 ? 'text-red-700' : ''}>{formatAmount(e.amount)}</Mono>
          </div>
          <div className="text-xs text-neutral-500">
            {e.external_reference && <span>Ref {e.external_reference} · </span>}
            {e.funding_event_id && (
              <span>
                Funding event <Mono>{e.funding_event_id.slice(0, 8)}</Mono> ·{' '}
              </span>
            )}
            {e.corrects_entry_id && (
              <span>
                Corrects <Mono>{e.corrects_entry_id.slice(0, 8)}</Mono> ·{' '}
              </span>
            )}
            {e.evidence_document_id && (
              <>
                <EvidenceLink caseId={caseId} documentId={e.evidence_document_id} /> ·{' '}
              </>
            )}
            <span>
              Recorded {formatDate(e.recorded_at)} · <Mono>{e.id.slice(0, 8)}</Mono>
            </span>
            {e.note && <p className="text-neutral-700">{e.note}</p>}
          </div>
        </li>
      ))}
    </ul>
  )
}

type FeeKind = RecordFeeEntryBody['entry_type']

function RecordFeeForm({ caseId, entries, events, onDone }: { caseId: string; entries: FeeEntryView[]; events: FundingEventView[]; onDone: () => void }) {
  const record = useRecordFee(caseId)
  const docs = useDocuments(caseId)
  const idem = useIdempotencyKey()
  const [kind, setKind] = useState<FeeKind>('expected_fee')
  const [amount, setAmount] = useState('')
  const [basis, setBasis] = useState('')
  const [evidence, setEvidence] = useState('')
  const [reference, setReference] = useState('')
  const [fundingEvent, setFundingEvent] = useState('')
  const [corrects, setCorrects] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const available = docs.data?.documents.filter((d) => d.availability === 'available') ?? []
  const correctable = entries.filter((e) => e.entry_type !== 'correction')
  const partnerEvents = events.filter((e) => e.kind === 'partner_confirmed_funding')

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const trimmedNote = note.trim()
      const money = parseMoney(amount, 'Amount')
      const link = fundingEvent ? { funding_event_id: fundingEvent } : {}
      let body: RecordFeeEntryBody
      if (kind === 'expected_fee') {
        body = { entry_type: kind, amount: money, basis: basis.trim(), ...link, ...(trimmedNote ? { note: trimmedNote } : {}) }
      } else if (kind === 'fee_collected') {
        body = {
          entry_type: kind,
          amount: money,
          evidence_document_id: evidence,
          ...(reference.trim() ? { external_reference: reference.trim() } : {}),
          ...link,
          ...(trimmedNote ? { note: trimmedNote } : {}),
        }
      } else {
        if (!trimmedNote) throw new Error('A correction needs a note')
        body = { entry_type: kind, corrects_entry_id: corrects, amount: money, note: trimmedNote, ...(evidence ? { evidence_document_id: evidence } : {}) }
      }
      await record.mutateAsync({ body, idempotencyKey: idem.key })
      idem.rotate()
      onDone()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  return (
    <form onSubmit={submit} className="mb-4 space-y-3 rounded-md bg-neutral-50 p-4">
      <Field label="Entry">
        <Select value={kind} onChange={(e) => setKind(e.target.value as FeeKind)}>
          <option value="expected_fee">{FEE_ENTRY_LABELS.expected_fee} (what the partner agreement says Tex is owed)</option>
          <option value="fee_collected">{FEE_ENTRY_LABELS.fee_collected} (a receipt Tex can evidence)</option>
          <option value="correction">{FEE_ENTRY_LABELS.correction} (signed delta to an earlier entry)</option>
        </Select>
      </Field>
      {kind === 'correction' && (
        <Field label="Corrects entry">
          <Select value={corrects} onChange={(e) => setCorrects(e.target.value)} required>
            <option value="">Choose the entry…</option>
            {correctable.map((e) => (
              <option key={e.id} value={e.id}>
                {FEE_ENTRY_LABELS[e.entry_type]} · {formatAmount(e.amount)} · {e.id.slice(0, 8)}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={kind === 'correction' ? 'Signed amount (USD)' : 'Amount (USD)'}>
          <Input value={amount} onChange={(e) => setAmount(e.target.value)} required placeholder={kind === 'correction' ? '-250.00' : '2500.00'} inputMode="decimal" />
        </Field>
        {kind === 'expected_fee' && (
          <Field label="Basis" hint="Which agreement clause or rate produced the number — text, not a formula.">
            <Input value={basis} onChange={(e) => setBasis(e.target.value)} required maxLength={500} />
          </Field>
        )}
        {kind === 'fee_collected' && (
          <Field label="External reference (optional)">
            <Input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} />
          </Field>
        )}
        {kind !== 'correction' && (
          <Field label="Funding event (optional)">
            <Select value={fundingEvent} onChange={(e) => setFundingEvent(e.target.value)}>
              <option value="">None</option>
              {partnerEvents.map((e) => (
                <option key={e.id} value={e.id}>
                  {formatAmount(e.amount)} · {formatDay(e.funded_on)} · {e.id.slice(0, 8)}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>
      {kind !== 'expected_fee' && (
        <Field label={kind === 'fee_collected' ? 'Evidence document' : 'Evidence document (optional)'} hint={kind === 'fee_collected' ? 'Remittance advice or bank confirmation. Required.' : undefined}>
          <Select value={evidence} onChange={(e) => setEvidence(e.target.value)} required={kind === 'fee_collected'}>
            <option value="">{kind === 'fee_collected' ? 'Choose a document…' : 'None'}</option>
            {available.map((d) => (
              <option key={d.id} value={d.id}>
                {d.original_filename} · {humanize(d.document_type)}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <Field label={kind === 'correction' ? 'Note' : 'Note (optional)'}>
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} required={kind === 'correction'} minLength={3} maxLength={2000} />
      </Field>
      {error && <Alert tone="error">{error}</Alert>}
      <Button type="submit" disabled={record.isPending}>
        Record
      </Button>
    </form>
  )
}
